import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import Image from '@/features/shared/Image';
import { MuiContainer, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import TwoColumnContent from '@/features/shared/TwoColumnContent';
import { Theme } from '@/utils/constants';
import { getNextId } from '@/utils/helpers';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { ChangeEvent, forwardRef, memo, useRef, useState } from 'react';
import { HorizontalLogosContentProps } from './HorizontalLogosContent.interface';
import useStyles from './HorizontalLogosContent.styles';

const a11yProps = (id: string, index: any) => {
  return {
    id: `${id}-logos-tab-${index}`,
    'aria-controls': `${id}-logos-tabpanel-${index}`,
  };
};

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
  id?: string;
  theme?: string;
}

const mapVariantToProps = ({ variant }: any) => {
  if (/^image$/.test(variant)) {
    return {
      type: 'featured',
    };
  }

  return {};
};

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^link$/.test(variant)) {
      return classes.link;
    }

    return '';
  };

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, panelName, classes, id, theme = 'light', ...other } = props;
  const rootClasses = clsx(classes.tabPanel, classes.tabPanelAnimated, `${theme}-theme`);
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${id}-${panelName}-${index}`}
      aria-labelledby={`${id}-logos-tab-${index}`}
      className={rootClasses}
      {...other}
    >
      {children}
    </div>
  );
};

const mapTabToPanelContent = ({
  variant,
  theme,
  classes,
  tab,
  isMobile,
  index,
}: {
  variant: string;
  theme: ThemeType;
  classes: any;
  tab: TabContentType;
  isMobile: boolean;
  index: number;
}) => {
  const { tabDetailContent } = tab;
  const { mediaContent } = tabDetailContent || {};

  if (/^content$/i.test(variant) && tabDetailContent) {
    const { superHeading, heading, detailContent } = tabDetailContent;

    return (
      <>
        {superHeading && (
          <MuiTypography variant="overline" component="h5" className={classes.superheading}>
            {superHeading}
          </MuiTypography>
        )}
        {heading && (
          <RichText
            customStyles
            theme={theme}
            className={clsx(classes.heading, `gtm-headline-${index}`, 'gtm-headline')}
            content={heading}
          />
        )}
        {isMobile && (
          <div className={classes.media}>
            <DetailContent
              content={mediaContent}
              theme={theme}
              mapVariantToProps={mapVariantToProps}
            />
          </div>
        )}
        <DetailContent
          content={detailContent}
          theme={theme}
          mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
        />
      </>
    );
  }

  if (/^media$/i.test(variant) && mediaContent) {
    return (
      !isMobile && (
        <DetailContent content={mediaContent} theme={theme} mapVariantToProps={mapVariantToProps} />
      )
    );
  }

  return null;
};

const TabPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme = Theme.light,
  variant = 'content',
  isMobile,
  id = '',
}: {
  classes: any;
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
  variant: string;
  isMobile: boolean;
  id: string;
}) => {
  return (
    <>
      {tabs.map((tab, i) => {
        const contentVariant = mapTabToPanelContent({
          theme,
          variant,
          classes,
          tab,
          isMobile,
          index: i,
        });

        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            id={id}
            classes={classes}
            theme={theme}
          >
            {contentVariant}
          </TabPanel>
        );
      })}
    </>
  );
};

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, title, imageClassName, ...tabProps } = props;
  const { logo } = title;
  const hasLogo = logo && logo.original;

  return (
    <div {...tabProps} ref={ref}>
      {hasLogo && (
        <Image
          image={logo}
          className={imageClassName}
          type="card"
          size="contain"
          position="center"
        />
      )}
    </div>
  );
});

const HorizontalLogosContent = ({
  theme = Theme.light,
  containerProps,
  tabs = [],
  tabColor,
  contentButtons,
}: Br.PropsWithContentButtons<HorizontalLogosContentProps>) => {
  const idTabPanelLeft = useRef(getNextId('horizontal-'));
  const idTabPanelRight = useRef(getNextId('horizontal-'));
  const classes = useStyles({});
  const [value, setValue] = useState(0);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };
  const rootClasses = clsx(classes.root, `${theme}-theme`);
  const rootTabClasses = clsx(classes.tab, 'gtm-tab', `bg-color-${tabColor}`);

  return (
    <ContentContainer
      {...containerProps}
      mobileTopBackgroundColor={containerProps?.topBackgroundColor}
      mobileBottomBackgroundColor={containerProps?.bottomBackgroundColor}
      topSpacing={containerProps?.topSpacing}
      bottomSpacing={containerProps?.bottomSpacing}
      data-component-variant="horizontal-logos-content"
    >
      <MuiContainer className="large">
        <div className={rootClasses}>
          <TwoColumnContent
            columnRatio="40-60"
            leftColumn={
              <>
                {contentButtons?.content}
                {tabs.length > 0 && (
                  <TabPanels
                    variant="content"
                    theme={theme}
                    tabs={tabs}
                    isMobile={isMobile}
                    value={value}
                    classes={classes}
                    id={idTabPanelLeft.current}
                  />
                )}
              </>
            }
            rightColumn={
              <>
                <TabPanels
                  variant="media"
                  theme={theme}
                  tabs={tabs}
                  isMobile={isMobile}
                  value={value}
                  classes={classes}
                  id={idTabPanelRight.current}
                />
                {tabs.length > 0 && (
                  <MuiTabs
                    orientation="horizontal"
                    variant="scrollable"
                    scrollButtons={isMobile ? 'on' : 'off'}
                    value={value}
                    onChange={handleChange}
                    aria-label="Horizontal Bottom MuiTabs"
                    className={classes.tabs}
                  >
                    {tabs.map(({ tabTitle }, i) => (
                      <MuiTab
                        // eslint-disable-next-line react/no-array-index-key
                        key={`tab_${i}`}
                        title={tabTitle}
                        {...a11yProps(idTabPanelRight.current, i)}
                        component={CustomTabComponent}
                        className={rootTabClasses}
                        imageClassName={classes.tabImage}
                      />
                    ))}
                  </MuiTabs>
                )}
              </>
            }
          />
        </div>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(HorizontalLogosContent);
